import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ImageService } from 'src/app/Core/services/image.service';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToasterService } from 'src/app/Core/services/toaster.service';
import { isSafeInput } from 'src/app/Core/utils/inputDataUtils';

@Component({
  selector: 'app-image-preview',
  templateUrl: './image-preview.component.html',
  styleUrls: ['./image-preview.component.css'],
  providers: [DatePipe]
})
export class ImagePreviewComponent implements OnInit {
  uploadedImage: string | ArrayBuffer | null = null;
  originalImage: string | ArrayBuffer | null = null;
  croppedImage: SafeUrl | null = null;
  imageCropEvent: ImageCroppedEvent | null = null;
  isCropping: boolean = false;
  imageWidth: number = 0;
  imageHeight: number = 0;
  isHovering: boolean = false;
  cropButtonClickCount: number = 0;
  redirectLink: string = '';
  buttonText: string = '';
  selectedImageType: string = 'explorer';
  selectedLanguage: string = 'en';

  enteredTitle: string = '';
  enteredDescription: string = '';
  enteredStartDate: Date;
  enteredEndDate: Date;
  minDate: Date;
  dateError: string | null = null;
  Error: string | null = null;
  redirectLinkError: string | null = null;
  invalidTitleError: String | null = null;
  invalidDescriptionError: String | null = null;
  imageSize: string = '0.00';
  fileExtension: string | null = null;
  fileMimeType: string | null = null;

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private imageService: ImageService,
    private datePipe: DatePipe,
    private http: HttpClient,
    private toaster: ToasterService
  ) {}

  ngOnInit() {
    this.uploadedImage = this.imageService.getImage();
    this.originalImage = this.uploadedImage;
    if (this.uploadedImage) {
      this.getImageSize(this.uploadedImage);
    }
    const fileInfo = this.getFileInfoFromBase64(this.uploadedImage);
    this.fileExtension = fileInfo.extension;
    this.fileMimeType = fileInfo.mimeType;
  }

  public getBase64Image(image: string | ArrayBuffer): string {
    if (typeof image === 'string') {
      return image;
    }
    return this.arrayBufferToBase64(image);
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.imageCropEvent = event;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  toggleCropping(): void {
    if (this.cropButtonClickCount % 2 === 0) {
      this.uploadedImage = this.originalImage;
      this.isCropping = true;
    } else {
      if (this.imageCropEvent?.objectUrl) {
        const croppedBlobUrl = this.imageCropEvent.objectUrl;
        fetch(croppedBlobUrl)
          .then(response => response.blob())
          .then(blob => this.blobToBase64(blob))
          .then(base64 => {
            this.uploadedImage = base64;
            this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(croppedBlobUrl);
          })
          .catch(error => {
            console.error('Error converting blob to base64:', error);
          });
        this.isCropping = false;
      }
    }
    this.cropButtonClickCount++;
  }

  resetImage(): void {
    this.uploadedImage = this.originalImage;
    this.croppedImage = null;
    this.isCropping = false;
  }

  async upload() {
    this.resetErrors();

    if (this.selectedImageType === "explorer") {
      if (!this.enteredTitle || !this.enteredDescription || !this.selectedLanguage || !this.buttonText || !this.redirectLink || !this.enteredStartDate || !this.enteredEndDate) {
        this.Error = 'All fields are mandatory.';
        return;
      }
      if (this.enteredTitle && !isSafeInput(this.enteredTitle)) {
        this.invalidTitleError = 'Provide valid Title';
        return;
      }
      if (this.enteredDescription && !isSafeInput(this.enteredDescription)) {
        this.invalidDescriptionError = 'Provide valid Description';
        return;
      }
    }
    if (this.selectedImageType === "banner") {
      if (!this.selectedLanguage || !this.redirectLink || !this.enteredStartDate || !this.enteredEndDate) {
        this.Error = 'All fields are mandatory.';
        return;
      }
    }

    const today = new Date();
    const enteredStart = new Date(this.enteredStartDate);
    const enteredEnd = new Date(this.enteredEndDate);

      today.setHours(0, 0, 0, 0);
      enteredStart.setHours(0, 0, 0, 0);
      enteredEnd.setHours(0, 0, 0, 0);

    if (enteredStart < today) {
      this.dateError = 'Start date cannot be in the past.';
    } else if (enteredEnd < enteredStart) {
      this.dateError = 'End date cannot be before start date.';
    }

    if (this.redirectLink && !this.isValidUrl(this.redirectLink)) {
      this.redirectLinkError = 'Please enter a valid URL.';
      return;
    }

    if (this.selectedImageType === 'banner' && (this.imageWidth !== 712 || this.imageHeight !== 200)) {
      this.Error = 'Banner image dimensions must be 712x200.';
      return;
    }

    const formattedStartDate = this.formatToISOString(this.enteredStartDate);
    const formattedEndDate = this.formatToISOString(this.enteredEndDate);

    const imageDimension = `${this.imageWidth}x${this.imageHeight}`;
    
    if (!this.Error && !this.dateError && !this.redirectLinkError && !this.invalidTitleError && !this.invalidDescriptionError) {
      const file =  this.uploadedImage;
      const fileName = `${this.enteredTitle}.${this.fileExtension}`;
      const boundary = 'ddc1446903258ae68fa70b82ea7c9ceb258a5f593e0acba952d9f77f1228';

      const metadata = {
        description: this.enteredDescription,
        end_date: formattedEndDate,
        start_date: formattedStartDate,
        language: this.selectedLanguage,
        image_type: this.selectedImageType,
        title: this.enteredTitle,
        read_more_link: this.redirectLink,
        button_text: this.buttonText,
        image_dimension: imageDimension,
        image_size: this.imageSize,
        content_type: this.fileMimeType,
        file_name: fileName,
      };

      const body = `
--${boundary}
Content-Id: metadata
Content-Type: application/json

${JSON.stringify(metadata)}
--${boundary}
Content-Disposition: attachment; filename=${fileName}
Content-Filename:${fileName}
Content-Id: media

${file}
--${boundary}--`;

      const headers = new HttpHeaders({
        'Content-Type': `multipart/related; boundary=${boundary}`
      });

      this.imageService.uploadImage(body, { headers }).subscribe(
        response => {
          this.toaster.openDialogBox("Image uploaded successfully",false);
          this.router.navigate(['/upload'])
        }, error => {
          console.error('Image upload failed', error);
          if(error.status === 403){
          this.toaster.openDialogBox("You don't have the required permission",true)
          }
        });
    }
    this.imageService.clearImage();
}
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private getFileInfoFromBase64(base64: string | ArrayBuffer | null): { extension: string | null, mimeType: string | null } {
    if (typeof base64 === 'string') {
      const mimeMatch = base64.match(/^data:(image\/[^;]+);base64,/);
      if (mimeMatch) {
        const mimeType = mimeMatch[1];
        const extension = mimeType.split('/')[1];
        return { extension, mimeType };
      }
    }
    return { extension: null, mimeType: null };  // Return null if the type cannot be determined
  }

  private formatToISOString(date: Date): string {
    const validDate = new Date(date);
    return validDate.toISOString();
  }

  resetErrors() {
    this.Error = null;
    this.dateError = null;
    this.redirectLinkError = null;
    this.invalidTitleError = null;
    this.invalidDescriptionError = null;
  }

  getImageDimensions(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    this.imageWidth = imgElement.naturalWidth;
    this.imageHeight = imgElement.naturalHeight;
  }

  getImageSize(image: string | ArrayBuffer) {
    const img = new Image();
    if (typeof image === 'string') {
        img.src = image;
        this.imageSize = this.calculateBase64Size(image);
    } else {
        const blob = new Blob([image]);
        this.imageSize = (blob.size / 1024).toFixed(2); 
        img.src = URL.createObjectURL(blob);
    }
}

private calculateBase64Size(base64: string): string {
  const padding = (base64.charAt(base64.length - 2) === '=') ? 2 : (base64.charAt(base64.length - 1) === '=') ? 1 : 0;
  const sizeInBytes = (base64.length * 3 / 4) - padding;
  return (sizeInBytes / 1024).toFixed(2);
}

handleImageTypeChange(newType: string) {
  if (newType === 'banner') {
    this.enteredTitle = '';
    this.enteredDescription = '';
    this.buttonText = '';
  }
}
}
